import { projectId, publicAnonKey } from './info';
import { supabase } from '@/lib/supabase';
import type {
  KickerTier,
  PaymentPeriod,
  MarkAsPaidCalculations,
  TeamMember,
} from '../../types/project';
import type {
  LeadSource,
  AccessChecklistItem,
  Client,
  ClientInput,
  ClientActivity,
  ClientForecast,
  ClientAccessStatus,
  ClientLifecycleStage,
  ClientTransitionInput,
  ClientPipelineSummary,
} from '../../types/crm';
import type {
  UserProfile,
  ProjectAccess,
  AdminNotification,
} from '../../types/user';
import type {
  WeeklyReport,
  WeeklyReportSummary,
} from '../../types/report';
import type {
  InternProfile,
  AdminTask,
  AdminBlocker,
  TaskNote,
  CreateInternTaskInput,
  UpdateInternTaskInput,
} from '../../features/intern-admin/types';
import type {
  DocumentSummary,
  DocumentDetail,
  DocumentShare,
  DocumentEngagementRow,
  CreateDocumentInput,
} from '../../types/documents';

// API client for server endpoints
class ProjectAPI {
  private baseUrl = `https://${projectId}.supabase.co/functions/v1/api`;

  // Get current user's auth token for authenticated requests
  private async getAuthToken(): Promise<string> {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) {
      throw new Error('No active session. Please sign in.');
    }
    return session.access_token;
  }

  private async makeRequest(endpoint: string, options: RequestInit = {}, retries = 2): Promise<any> {
    const url = `${this.baseUrl}${endpoint}`;
    const token = await this.getAuthToken();

    let lastError: Error | null = null;

    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        const response = await fetch(url, {
          ...options,
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            ...options.headers,
          },
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          const errorMessage = errorData.error || `HTTP ${response.status}: ${response.statusText}`;
          const details = errorData.details ? ` (${errorData.details})` : '';

          // Don't retry 4xx errors (client errors) except for 429 (rate limit)
          if (response.status >= 400 && response.status < 500 && response.status !== 429) {
            throw new Error(errorMessage + details);
          }

          // For 5xx errors, retry
          lastError = new Error(errorMessage + details);
          if (attempt < retries) {
            console.warn(`Request to ${endpoint} failed (attempt ${attempt + 1}/${retries + 1}), retrying...`);
            await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1))); // Exponential backoff
            continue;
          }
          throw lastError;
        }

        return response.json();
      } catch (error) {
        // Network errors - retry
        if (error instanceof TypeError && error.message.includes('fetch')) {
          lastError = new Error(`Network error: Unable to connect to API`);
          if (attempt < retries) {
            console.warn(`Network error on ${endpoint} (attempt ${attempt + 1}/${retries + 1}), retrying...`);
            await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)));
            continue;
          }
        }
        throw error;
      }
    }

    throw lastError || new Error('Request failed');
  }

  // ============================================
  // Health Check
  // ============================================

  async healthCheck(): Promise<{ status: string; database?: string; error?: string }> {
    try {
      const data = await this.makeRequest('/health', {}, 0); // No retries for health check
      return data;
    } catch (error) {
      return {
        status: 'unreachable',
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  // ============================================
  // Team Member Endpoints
  // ============================================

  async getTeamMembers(options?: { collective?: boolean; contractor?: boolean }): Promise<TeamMember[]> {
    const params = new URLSearchParams();
    if (options?.collective) params.set('collective', 'true');
    if (options?.contractor) params.set('contractor', 'true');
    const query = params.toString() ? `?${params}` : '';
    const data = await this.makeRequest(`/team-members${query}`);
    return data.teamMembers || [];
  }

  async createTeamMember(params: {
    name: string;
    email?: string;
    is_collective?: boolean;
    is_contractor?: boolean;
  }): Promise<TeamMember> {
    const data = await this.makeRequest('/team-members', {
      method: 'POST',
      body: JSON.stringify(params),
    });
    return data.teamMember;
  }

  async updateTeamMember(id: string, updates: Partial<TeamMember>): Promise<TeamMember> {
    const data = await this.makeRequest(`/team-members/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
    return data.teamMember;
  }

  // Get collective partners only (the 5 business partners)
  async getCollectivePartners(): Promise<TeamMember[]> {
    return this.getTeamMembers({ collective: true });
  }

  // Get contractors only
  async getContractors(): Promise<TeamMember[]> {
    return this.getTeamMembers({ contractor: true });
  }

  // ============================================
  // Kicker Tier Endpoints
  // ============================================

  async getKickerTiers(): Promise<KickerTier[]> {
    const data = await this.makeRequest('/kicker-tiers');
    return data.tiers || [];
  }

  // ============================================
  // Payment Period Endpoints
  // ============================================

  async getPaymentPeriods(): Promise<PaymentPeriod[]> {
    const data = await this.makeRequest('/payments');
    return data.payments || [];
  }

  async getPaymentPeriod(monthYear: string, isOffCycle: boolean = false): Promise<PaymentPeriod> {
    const query = isOffCycle ? '?off_cycle=true' : '';
    const data = await this.makeRequest(`/payments/${monthYear}${query}`);
    return data.payment;
  }

  async markMonthAsPaid(monthYear: string, paidBy: string, calculations: MarkAsPaidCalculations, isOffCycle: boolean = false): Promise<PaymentPeriod> {
    const data = await this.makeRequest(`/payments/${monthYear}/mark-paid`, {
      method: 'POST',
      body: JSON.stringify({ paidBy, calculations, isOffCycle }),
    });
    return data.payment;
  }

  async deletePaymentPeriod(monthYear: string, isOffCycle: boolean = false): Promise<void> {
    const query = isOffCycle ? '?off_cycle=true' : '';
    await this.makeRequest(`/payments/${monthYear}${query}`, {
      method: 'DELETE',
    });
  }

  async updatePaymentSnapshot(snapshotId: string, updates: {
    revenue?: number;
    profit_margin?: number;
    operations_overhead_pct?: number;
    operations_compensation_per_owner?: number;
    calculated_gross_profit?: number;
  }): Promise<any> {
    const data = await this.makeRequest(`/payments/snapshots/${snapshotId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
    return data.snapshot;
  }

  // ============================================
  // CRM Endpoints
  // ============================================

  // Lead Sources
  async getLeadSources(): Promise<LeadSource[]> {
    const data = await this.makeRequest('/crm/lead-sources');
    return data.leadSources || [];
  }

  // Access Checklist
  async getAccessChecklist(): Promise<AccessChecklistItem[]> {
    const data = await this.makeRequest('/crm/access-checklist');
    return data.checklistItems || [];
  }

  // Note: Legacy prospect endpoints have been removed.
  // All CRM functionality is now handled via the unified clients table
  // with lifecycle stages: 'lead', 'prospect', 'active', 'churned'
  // Use getClients(), createClient(), updateClient(), etc. instead.

  // ============================================
  // Unified Client Endpoints (New Lifecycle Model)
  // ============================================

  // Get all clients with optional filtering
  async getClients(options?: {
    stage?: ClientLifecycleStage;
    excludeChurned?: boolean;
    withFinancials?: boolean;
  }): Promise<Client[]> {
    const params = new URLSearchParams();
    if (options?.stage) params.set('stage', options.stage);
    if (options?.excludeChurned) params.set('excludeChurned', 'true');
    if (options?.withFinancials) params.set('with_financials', 'true');
    const query = params.toString() ? `?${params}` : '';
    const data = await this.makeRequest(`/crm/clients${query}`);
    return data.clients || [];
  }

  // Get a single client with related data
  async getClient(id: string): Promise<Client & {
    activities: ClientActivity[];
    forecasts: ClientForecast[];
    accessStatus: ClientAccessStatus[];
  }> {
    const data = await this.makeRequest(`/crm/clients/${id}`);
    return data.client;
  }

  // Create a new client
  async createClient(client: ClientInput): Promise<Client> {
    const data = await this.makeRequest('/crm/clients', {
      method: 'POST',
      body: JSON.stringify(client),
    });
    return data.client;
  }

  // Update client fields
  async updateClient(id: string, updates: Partial<ClientInput>): Promise<Client> {
    const data = await this.makeRequest(`/crm/clients/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
    return data.client;
  }

  // Transition client to a new lifecycle stage
  async transitionClient(id: string, transition: ClientTransitionInput): Promise<Client> {
    const data = await this.makeRequest(`/crm/clients/${id}/transition`, {
      method: 'POST',
      body: JSON.stringify(transition),
    });
    return data.client;
  }

  // Get client activities
  async getClientActivities(clientId: string): Promise<ClientActivity[]> {
    const data = await this.makeRequest(`/crm/clients/${clientId}/activities`);
    return data.activities || [];
  }

  // Add client activity
  async addClientActivity(clientId: string, activity: {
    activity_type: string;
    content: string;
  }, createdBy?: string): Promise<ClientActivity> {
    const data = await this.makeRequest(`/crm/clients/${clientId}/activities`, {
      method: 'POST',
      body: JSON.stringify({ ...activity, created_by: createdBy }),
    });
    return data.activity;
  }

  // Get client forecasts
  async getClientForecasts(clientId: string): Promise<ClientForecast[]> {
    const data = await this.makeRequest(`/crm/clients/${clientId}/forecasts`);
    return data.forecasts || [];
  }

  // Update client forecast
  async updateClientForecast(clientId: string, forecastMonth: string, updates: {
    forecasted_revenue?: number;
    is_manual_override?: boolean;
    override_reason?: string;
  }): Promise<ClientForecast> {
    const data = await this.makeRequest(`/crm/clients/${clientId}/forecasts/${forecastMonth}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
    return data.forecast;
  }

  // Get weighted pipeline summary by lifecycle stage
  async getClientPipelineSummary(): Promise<ClientPipelineSummary[]> {
    const data = await this.makeRequest('/crm/clients/pipeline-summary');
    return data.summary || [];
  }

  // Get monthly forecast totals
  async getMonthlyForecast(): Promise<{
    forecast_month: string;
    total_forecasted: number;
    total_weighted: number;
    client_count: number;
  }[]> {
    const data = await this.makeRequest('/crm/clients/monthly-forecast');
    return data.forecasts || [];
  }

  // Update client financial configuration (replaces project financial updates)
  async updateClientFinancial(id: string, updates: {
    confirmed_monthly_revenue?: number;
    profit_margin?: number;
    operations_overhead_percentage?: number;
    operations_compensation_per_owner?: number;
    strategy_sponsor_id?: string;
    strategySponsor?: string;
    operationsOwners?: string[];
    tacticalAssignments?: Array<{
      name?: string;
      role_name?: string;
      assignedOwner?: string;
      amount?: number;
      monthly_amount?: number;
      handlesKickers?: boolean;
      handles_kickers?: boolean;
    }>;
    performanceKickers?: Array<{
      name: string;
      marketingSpend?: number;
      marketing_spend?: number;
      selectedTier?: string;
      sharePercentage?: number;
      specialist_share_percentage?: number;
      eligiblePeople?: Array<{ name: string; type: string; role?: string }>;
      eligible_people?: Array<{ name: string; type: string; role?: string }>;
    }>;
  }): Promise<Client> {
    const data = await this.makeRequest(`/crm/clients/${id}/financial`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
    return data.client;
  }

  // Set ad-hoc revenue for a variable-revenue client for a specific month
  async setAdHocRevenue(clientId: string, month: string, amount: number): Promise<ClientForecast> {
    const data = await this.makeRequest(`/crm/clients/${clientId}/ad-hoc-revenue`, {
      method: 'POST',
      body: JSON.stringify({ month, amount }),
    });
    return data.forecast;
  }

  // ============================================
  // User Management Endpoints (Super Admin Only)
  // ============================================

  async getUsers(): Promise<UserProfile[]> {
    const data = await this.makeRequest('/users');
    return data.users || [];
  }

  async getUser(id: string): Promise<UserProfile & { projectAccess: ProjectAccess[] }> {
    const data = await this.makeRequest(`/users/${id}`);
    return data.user;
  }

  async inviteUser(params: {
    email: string;
    displayName?: string;
    role: string;
    projectAccess?: Array<{
      projectId: string;
      permission?: string;
      canViewFinancials?: boolean;
      canViewPayments?: boolean;
      canViewTeam?: boolean;
    }>;
  }): Promise<{ user: UserProfile; emailSent: boolean; emailError?: string }> {
    const data = await this.makeRequest('/users/invite', {
      method: 'POST',
      body: JSON.stringify(params),
    });
    return {
      user: data.user,
      emailSent: data.emailSent ?? true,
      emailError: data.emailError,
    };
  }

  async updateUser(id: string, updates: {
    display_name?: string;
    role?: string;
    status?: string;
    owner_id?: string;
  }): Promise<UserProfile> {
    const data = await this.makeRequest(`/users/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
    return data.user;
  }

  async deactivateUser(id: string): Promise<void> {
    await this.makeRequest(`/users/${id}`, {
      method: 'DELETE',
    });
  }

  async resendInvite(userId: string): Promise<void> {
    await this.makeRequest(`/users/${userId}/resend-invite`, {
      method: 'POST',
    });
  }

  async cancelInvite(userId: string): Promise<void> {
    await this.makeRequest(`/users/${userId}/cancel-invite`, {
      method: 'DELETE',
    });
  }

  async getUserAccess(userId: string): Promise<ProjectAccess[]> {
    const data = await this.makeRequest(`/users/${userId}/access`);
    return data.projectAccess || [];
  }

  async grantProjectAccess(userId: string, params: {
    projectId: string;
    permission?: string;
    canViewFinancials?: boolean;
    canViewPayments?: boolean;
    canViewTeam?: boolean;
    expiresAt?: string;
  }): Promise<ProjectAccess> {
    const data = await this.makeRequest(`/users/${userId}/access`, {
      method: 'POST',
      body: JSON.stringify(params),
    });
    return data.projectAccess;
  }

  async revokeProjectAccess(userId: string, projectId: string): Promise<void> {
    await this.makeRequest(`/users/${userId}/access/${projectId}`, {
      method: 'DELETE',
    });
  }

  // ============================================
  // Notification Endpoints (Super Admin Only)
  // ============================================

  async getNotifications(unreadOnly?: boolean): Promise<{
    notifications: AdminNotification[];
    unreadCount: number;
  }> {
    const query = unreadOnly ? '?unread=true' : '';
    const data = await this.makeRequest(`/notifications${query}`);
    return data;
  }

  async markNotificationAsRead(id: string): Promise<void> {
    await this.makeRequest(`/notifications/${id}/read`, {
      method: 'POST',
    });
  }

  async markAllNotificationsAsRead(): Promise<void> {
    await this.makeRequest('/notifications/mark-all-read', {
      method: 'POST',
    });
  }

  // ============================================
  // Current User Profile Endpoints
  // ============================================

  async getCurrentUser(): Promise<UserProfile & { projectAccess: ProjectAccess[] }> {
    const data = await this.makeRequest('/me');
    return data.user;
  }

  async updateCurrentUserProfile(updates: { display_name?: string }): Promise<UserProfile> {
    const data = await this.makeRequest('/me', {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
    return data.user;
  }

  // ============================================
  // Weekly Reports Endpoints
  // ============================================

  async getReports(): Promise<WeeklyReportSummary[]> {
    const data = await this.makeRequest('/reports');
    return data.reports || [];
  }

  async getReport(id: string): Promise<WeeklyReport> {
    const data = await this.makeRequest(`/reports/${id}`);
    return data;
  }

  async createReport(report: {
    week_start_date: string;
    week_end_date: string;
    raw_markdown?: string;
    content?: Record<string, unknown>;
    status?: string;
  }): Promise<WeeklyReport> {
    const data = await this.makeRequest('/reports', {
      method: 'POST',
      body: JSON.stringify(report),
    });
    return data;
  }

  async updateReport(id: string, updates: {
    status?: string;
    published_at?: string;
    published_by?: string;
    raw_markdown?: string;
    content?: Record<string, unknown>;
  }): Promise<WeeklyReport> {
    const data = await this.makeRequest(`/reports/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
    return data;
  }

  // Get projects by loading clients and mapping to project shape
  async getProjects(): Promise<{ id: string; projectName: string; status: string }[]> {
    const clients = await this.getClients({});
    return clients.map(c => ({
      id: c.id,
      projectName: c.company_name,
      status: c.lifecycle_stage === 'active' ? 'active' :
              c.lifecycle_stage === 'paused' ? 'inactive' :
              c.lifecycle_stage === 'churned' ? 'canceled' : 'prospective',
    }));
  }

  // ============================================
  // Intern Admin Console (super_admin only)
  // ============================================

  async getInterns(): Promise<InternProfile[]> {
    const data = await this.makeRequest('/interns');
    return data.interns || [];
  }

  async getInternTasks(filters?: { assignee?: string; status?: string }): Promise<AdminTask[]> {
    const params = new URLSearchParams();
    if (filters?.assignee) params.set('assignee', filters.assignee);
    if (filters?.status) params.set('status', filters.status);
    const query = params.toString() ? `?${params}` : '';
    const data = await this.makeRequest(`/intern/tasks${query}`);
    return data.tasks || [];
  }

  async createInternTask(task: CreateInternTaskInput): Promise<AdminTask> {
    const data = await this.makeRequest('/intern/tasks', {
      method: 'POST',
      body: JSON.stringify(task),
    });
    return data.task;
  }

  async updateInternTask(id: string, updates: UpdateInternTaskInput): Promise<AdminTask> {
    const data = await this.makeRequest(`/intern/tasks/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
    return data.task;
  }

  async getInternTaskNotes(taskId: string): Promise<TaskNote[]> {
    const data = await this.makeRequest(`/intern/tasks/${taskId}/notes`);
    return data.notes || [];
  }

  async addInternTaskNote(taskId: string, body: string, isBlocker = false): Promise<TaskNote> {
    const data = await this.makeRequest(`/intern/tasks/${taskId}/notes`, {
      method: 'POST',
      body: JSON.stringify({ body, is_blocker: isBlocker }),
    });
    return data.note;
  }

  async getInternBlockers(): Promise<AdminBlocker[]> {
    const data = await this.makeRequest('/intern/blockers');
    return data.blockers || [];
  }

  async resolveInternBlocker(noteId: string): Promise<TaskNote> {
    const data = await this.makeRequest(`/intern/notes/${noteId}/resolve`, {
      method: 'POST',
    });
    return data.note;
  }

  // ============================================
  // Documents (shared pages) — admin/owner only
  // ============================================

  async getDocuments(filters?: { prospect_id?: string; client_id?: string; status?: string }): Promise<DocumentSummary[]> {
    const params = new URLSearchParams();
    if (filters?.prospect_id) params.set('prospect_id', filters.prospect_id);
    if (filters?.client_id) params.set('client_id', filters.client_id);
    if (filters?.status) params.set('status', filters.status);
    const query = params.toString() ? `?${params}` : '';
    const data = await this.makeRequest(`/documents${query}`);
    return data.documents || [];
  }

  async getDocument(id: string): Promise<DocumentDetail> {
    return this.makeRequest(`/documents/${id}`);
  }

  async createDocument(doc: CreateDocumentInput): Promise<DocumentDetail> {
    return this.makeRequest('/documents', {
      method: 'POST',
      body: JSON.stringify(doc),
    });
  }

  async updateDocument(id: string, updates: Partial<CreateDocumentInput>): Promise<DocumentDetail> {
    return this.makeRequest(`/documents/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  }

  async deleteDocument(id: string): Promise<void> {
    await this.makeRequest(`/documents/${id}`, { method: 'DELETE' });
  }

  async createDocumentShare(documentId: string, opts?: { recipient_label?: string; passcode?: string; expires_at?: string }): Promise<DocumentShare> {
    return this.makeRequest(`/documents/${documentId}/shares`, {
      method: 'POST',
      body: JSON.stringify(opts || {}),
    });
  }

  async updateDocumentShare(id: string, updates: { revoked?: boolean; expires_at?: string | null; recipient_label?: string }): Promise<DocumentShare> {
    return this.makeRequest(`/document-shares/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  }

  async getDocumentEngagement(id: string): Promise<{ engagement: DocumentEngagementRow[]; total_views: number }> {
    return this.makeRequest(`/documents/${id}/engagement`);
  }
}

export const projectAPI = new ProjectAPI();

// ============================================================
// Connects API
// ============================================================

import type {
  ConnectsDashboardData,
  ContactDetailResponse,
  CompanyDetailResponse,
  DailyDigest,
  ConnectsContact,
  ConnectsCompany,
} from '../../types/connects';

class ConnectsAPI {
  private baseUrl = `https://${projectId}.supabase.co/functions/v1/api`;

  private async getAuthToken(): Promise<string> {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) throw new Error('No active session. Please sign in.');
    return session.access_token;
  }

  private async get<T>(path: string, params?: Record<string, string>): Promise<T> {
    const token = await this.getAuthToken();
    const url = new URL(`${this.baseUrl}${path}`);
    if (params) Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, v));
    const res = await fetch(url.toString(), {
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error ?? `HTTP ${res.status}`);
    }
    return res.json();
  }

  async getDashboard(): Promise<ConnectsDashboardData> {
    return this.get('/connects/dashboard');
  }

  async getContacts(opts?: { q?: string; limit?: number; offset?: number }): Promise<{ contacts: ConnectsContact[]; total: number }> {
    const params: Record<string, string> = {};
    if (opts?.q) params.q = opts.q;
    if (opts?.limit) params.limit = String(opts.limit);
    if (opts?.offset) params.offset = String(opts.offset);
    return this.get('/connects/contacts', params);
  }

  async getContact(id: string): Promise<ContactDetailResponse> {
    return this.get(`/connects/contacts/${id}`);
  }

  async getCompanies(opts?: { q?: string; limit?: number; offset?: number }): Promise<{ companies: ConnectsCompany[]; total: number }> {
    const params: Record<string, string> = {};
    if (opts?.q) params.q = opts.q;
    if (opts?.limit) params.limit = String(opts.limit);
    if (opts?.offset) params.offset = String(opts.offset);
    return this.get('/connects/companies', params);
  }

  async getCompany(id: string): Promise<CompanyDetailResponse> {
    return this.get(`/connects/companies/${id}`);
  }

  async getDigests(): Promise<{ digests: DailyDigest[] }> {
    return this.get('/connects/digests');
  }

  async getDigestToday(asOf?: string): Promise<any> {
    const params = asOf ? { as_of: asOf } : undefined;
    return this.get('/connects/digest/today', params);
  }
}

export const connectsAPI = new ConnectsAPI();

// ============================================================
// Prospects API — pre-pipeline brand intelligence
// ============================================================

import type { Prospect, ProspectStatus } from '../../types/prospects';

class ProspectsAPI {
  async getAll(opts?: {
    q?: string;
    priority_tier?: string;
    status?: ProspectStatus;
    limit?: number;
    offset?: number;
  }): Promise<{ prospects: Prospect[]; total: number }> {
    let query = supabase
      .from('prospects')
      .select('*', { count: 'exact' })
      .order('final_priority_score', { ascending: false });

    if (opts?.q && opts.q.length >= 2) {
      query = query.or(
        `brand.ilike.%${opts.q}%,category.ilike.%${opts.q}%,known_founder_or_ceo.ilike.%${opts.q}%,sean_matched_contact.ilike.%${opts.q}%`
      );
    }
    if (opts?.priority_tier) {
      query = query.eq('priority_tier', opts.priority_tier);
    }
    if (opts?.status) {
      query = query.eq('status', opts.status);
    }
    if (opts?.limit) {
      query = query.range(opts.offset ?? 0, (opts.offset ?? 0) + opts.limit - 1);
    }

    const { data, error, count } = await query;
    if (error) throw new Error(error.message);
    return { prospects: (data as Prospect[]) ?? [], total: count ?? 0 };
  }

  async getOne(id: string): Promise<Prospect> {
    const { data, error } = await supabase
      .from('prospects')
      .select('*')
      .eq('id', id)
      .single();
    if (error) throw new Error(error.message);
    return data as Prospect;
  }

  async update(id: string, updates: Partial<Pick<Prospect, 'status' | 'notes' | 'deck_url'>>): Promise<Prospect> {
    const { data, error } = await supabase
      .from('prospects')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    if (error) throw new Error(error.message);
    return data as Prospect;
  }

  // Creating and deleting prospects became possible in 20260814000001 — before
  // that `prospects` had RLS on with no INSERT or DELETE policy, so both were
  // denied for every role including super_admin.
  async create(input: { brand: string; category?: string; website?: string; status?: ProspectStatus }): Promise<Prospect> {
    const { data, error } = await supabase
      .from('prospects')
      .insert(input)
      .select()
      .single();
    if (error) throw new Error(error.message);
    return data as Prospect;
  }

  async remove(id: string): Promise<void> {
    const { error } = await supabase.from('prospects').delete().eq('id', id);
    if (error) throw new Error(error.message);
  }
}

export const prospectsAPI = new ProspectsAPI();

// ============================================
// Journey pipeline (intro → close)
// ============================================

import type { JourneyProspect, JourneyAction, JourneyStage } from '../../types/journey';

class JourneyAPI {
  async getAll(opts?: {
    q?: string;
    stage?: JourneyStage;
    limit?: number;
    offset?: number;
  }): Promise<{ journeys: JourneyProspect[]; total: number }> {
    let query = supabase
      .from('journey_prospects')
      .select('*', { count: 'exact' })
      .order('updated_at', { ascending: false, nullsFirst: false });

    if (opts?.q && opts.q.length >= 2) {
      query = query.or(
        `prospect_email.ilike.%${opts.q}%,company_name.ilike.%${opts.q}%,person_name.ilike.%${opts.q}%,connector_email.ilike.%${opts.q}%`
      );
    }
    if (opts?.stage) {
      query = query.eq('stage', opts.stage);
    }
    if (opts?.limit) {
      query = query.range(opts.offset ?? 0, (opts.offset ?? 0) + opts.limit - 1);
    }

    const { data, error, count } = await query;
    if (error) throw new Error(error.message);
    return { journeys: (data as JourneyProspect[]) ?? [], total: count ?? 0 };
  }

  async getOne(id: string): Promise<JourneyProspect> {
    const { data, error } = await supabase
      .from('journey_prospects')
      .select('*')
      .eq('id', id)
      .single();
    if (error) throw new Error(error.message);
    return data as JourneyProspect;
  }

  async update(
    id: string,
    updates: Partial<Pick<JourneyProspect, 'stage' | 'company_name' | 'person_name' | 'connector_email'>>
  ): Promise<JourneyProspect> {
    const { data, error } = await supabase
      .from('journey_prospects')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    if (error) throw new Error(error.message);
    return data as JourneyProspect;
  }

  // The worker's worklog for one journey. Read-only: journey_actions grants
  // `authenticated` SELECT and nothing else.
  async getActions(journeyId: string): Promise<JourneyAction[]> {
    const { data, error } = await supabase
      .from('journey_actions')
      .select('*')
      .eq('journey_id', journeyId)
      .order('created_at', { ascending: false });
    if (error) throw new Error(error.message);
    return (data as JourneyAction[]) ?? [];
  }
}

export const journeyAPI = new JourneyAPI();